import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Check, X, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTIES = {
  easy: { label: 'Easy', columns: 3, color: 'green', perRow: 1.45, multiplier: '1.45x/row' },
  medium: { label: 'Medium', columns: 3, color: 'yellow', perRow: 2.90, multiplier: '2.90x/row' },
  hard: { label: 'Hard', columns: 4, color: 'red', perRow: 3.80, multiplier: '3.80x/row' }
};

export default function Towers() {
  const { user, token, refreshUser } = useAuth();
  const [betAmount, setBetAmount] = useState(10);
  const [difficulty, setDifficulty] = useState('easy');
  const [gameId, setGameId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [currentRow, setCurrentRow] = useState(0);
  const [towerLayout, setTowerLayout] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [currentMultiplier, setCurrentMultiplier] = useState(1.0);
  const [nextMultiplier, setNextMultiplier] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'RBX.Casino | Towers';
  }, []);

  const startGame = async () => {
    if (!token) return toast.error('Please login first');
    if (betAmount < 1) return toast.error('Minimum bet is R$1');
    if (betAmount > (user?.site_balance || 0)) return toast.error('Insufficient balance');
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/towers/create`,
        { bet_amount: betAmount, difficulty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGameId(response.data.game_id);
      setGameState('active');
      setIsPlaying(true);
      setCurrentRow(0);
      setTowerLayout([]);
      setSelectedColumns([]);
      setCurrentMultiplier(1.0);
      setNextMultiplier(response.data.next_multiplier);
      refreshUser();
      toast.success('Game started! Climb the tower!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  const climb = async (column) => {
    if (!isPlaying || loading) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/towers/climb`,
        { game_id: gameId, column },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedColumns([...selectedColumns, { row: currentRow, column }]);
      
      if (!response.data.is_safe) {
        setTowerLayout(response.data.tower_layout);
        setGameState('lost');
        setIsPlaying(false);
        toast.error('You fell! Game over.');
        refreshUser();
      } else if (response.data.game_over) {
        setTowerLayout(response.data.tower_layout);
        setGameState('won');
        setIsPlaying(false);
        setCurrentMultiplier(response.data.multiplier);
        toast.success(`Reached the top! Won R$${response.data.payout.toFixed(2)}`);
        refreshUser();
      } else {
        setCurrentRow(response.data.current_row);
        setCurrentMultiplier(response.data.multiplier);
        setNextMultiplier(response.data.next_multiplier);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to climb');
    } finally {
      setLoading(false);
    }
  };

  const cashout = async () => {
    if (!isPlaying || currentRow === 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/towers/cashout?game_id=${gameId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTowerLayout(response.data.tower_layout);
      setGameState('won');
      setIsPlaying(false);
      toast.success(`Cashed out R$${response.data.payout.toFixed(2)} at ${response.data.multiplier}x!`);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cash out');
    } finally {
      setLoading(false);
    }
  };

  const columns = DIFFICULTIES[difficulty].columns;
  const perRow = DIFFICULTIES[difficulty].perRow;

  // Potential Robux win for clearing a given row (0-indexed), mirrors backend formula.
  const rowWin = (row) => {
    const mult = Math.round(Math.pow(perRow, row + 1) * 0.99 * 100) / 100;
    return Number(betAmount || 0) * mult;
  };

  const getCellState = (row, col) => {
    const selected = selectedColumns.find(s => s.row === row && s.column === col);
    if (towerLayout.length > 0) {
      const isSafe = towerLayout[row]?.includes(col);
      if (selected) {
        return isSafe ? 'correct' : 'wrong';
      }
      if (row < currentRow || gameState !== 'active') {
        return isSafe ? 'safe' : 'trap';
      }
    }
    if (selected) return 'selected';
    if (row === currentRow && isPlaying) return 'clickable';
    return 'locked';
  };

  const getCellStyle = (state) => {
    switch (state) {
      case 'correct': return 'bg-green-500/30 border-green-500';
      case 'wrong': return 'bg-red-500/30 border-red-500';
      case 'safe': return 'bg-green-500/20 border-green-500/50';
      case 'trap': return 'bg-red-500/20 border-red-500/50';
      case 'selected': return 'bg-blue-500/30 border-blue-500';
      case 'clickable': return 'bg-slate-700/50 border-blue-500 hover:bg-blue-500/20 cursor-pointer';
      default: return 'bg-slate-800/30 border-slate-700/50';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
        <Building2 className="text-blue-500" /> Towers
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tower Grid */}
        <div className="lg:col-span-2">
          <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
            <div className="flex flex-col-reverse gap-1">
              {Array.from({ length: 10 }).map((_, row) => (
                <div key={row} className="flex gap-1 items-center">
                  <span className="w-8 text-right text-slate-500 text-sm">{row + 1}</span>
                  <div className="flex-1 flex gap-1">
                    {Array.from({ length: columns }).map((_, col) => {
                      const state = getCellState(row, col);
                      return (
                        <motion.button
                          key={col}
                          onClick={() => state === 'clickable' && climb(col)}
                          disabled={state !== 'clickable'}
                          className={`flex-1 h-12 rounded-lg border-2 flex items-center justify-center transition-all ${getCellStyle(state)}`}
                          whileHover={state === 'clickable' ? { scale: 1.05 } : {}}
                          whileTap={state === 'clickable' ? { scale: 0.95 } : {}}
                        >
                          {state === 'correct' && <Check className="w-5 h-5 text-green-400" />}
                          {state === 'wrong' && <X className="w-5 h-5 text-red-400" />}
                          {state === 'safe' && <Check className="w-4 h-4 text-green-400/60" />}
                          {state === 'trap' && <X className="w-4 h-4 text-red-400/60" />}
                          {isPlaying && (state === 'clickable' || state === 'locked') && (
                            <span className={`text-[11px] font-bold ${state === 'clickable' ? 'text-green-400' : 'text-slate-500'}`}>
                              R${rowWin(row).toFixed(0)}
                            </span>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                  <span className="w-10 text-left text-slate-500 text-xs">
                    {row === currentRow && isPlaying ? '◄' : ''}
                  </span>
                </div>
              ))}
            </div>

            {isPlaying && (
              <div className="mt-4 text-center">
                <p className="text-slate-400">Floor: <span className="text-blue-400 font-bold">{currentRow}/10</span></p>
                <p className="text-slate-400">Multiplier: <span className="text-green-400 font-bold text-xl">{currentMultiplier}x</span></p>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
            {!isPlaying ? (
              <>
                <div className="mb-4">
                  <label className="text-slate-400 text-sm mb-2 block">Bet Amount</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white"
                      min={1}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-slate-400 text-sm mb-2 block">Difficulty</label>
                  <div className="space-y-2">
                    {Object.entries(DIFFICULTIES).map(([key, val]) => (
                      <button
                        key={key}
                        onClick={() => setDifficulty(key)}
                        className={`w-full py-3 px-4 rounded-xl border-2 flex justify-between items-center transition-all ${
                          difficulty === key
                            ? `bg-${val.color}-500/20 border-${val.color}-500 text-white`
                            : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        <span>{val.label}</span>
                        <span className="text-sm">{val.multiplier}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={startGame}
                  disabled={loading || !user}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded-xl text-white font-bold transition-colors"
                >
                  {loading ? 'Starting...' : 'Start Climbing'}
                </button>
              </>
            ) : (
              <>
                <div className="text-center mb-4">
                  <p className="text-slate-400">Bet: <span className="text-white font-bold">R${betAmount}</span></p>
                  <p className="text-slate-400">Difficulty: <span className="text-blue-400 font-bold capitalize">{difficulty}</span></p>
                </div>

                <div className="bg-slate-800/50 rounded-xl p-4 mb-4 text-center">
                  <p className="text-slate-400 text-sm">Potential Win</p>
                  <p className="text-2xl font-bold text-green-400">R${(betAmount * currentMultiplier).toFixed(2)}</p>
                </div>

                <button
                  onClick={cashout}
                  disabled={loading || currentRow === 0}
                  className="w-full py-3 bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-700 rounded-xl text-white font-bold transition-colors"
                >
                  {loading ? 'Processing...' : `Cash Out ${currentMultiplier}x`}
                </button>
              </>
            )}
          </div>

          {(gameState === 'lost' || gameState === 'won') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`${gameState === 'won' ? 'bg-green-500/20 border-green-500/50' : 'bg-red-500/20 border-red-500/50'} border rounded-xl p-4 text-center`}
            >
              <p className={`${gameState === 'won' ? 'text-green-400' : 'text-red-400'} font-bold`}>
                {gameState === 'won' ? 'You Won!' : 'You Fell!'}
              </p>
              {gameState === 'won' && <p className="text-white text-lg">R${(betAmount * currentMultiplier).toFixed(2)}</p>}
              <button
                onClick={() => { setGameState(null); setTowerLayout([]); setSelectedColumns([]); setCurrentRow(0); }}
                className="mt-2 px-4 py-2 bg-slate-800 rounded-lg text-white text-sm"
              >
                Play Again
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
